import { Card, CardContent, Typography, CircularProgress, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import { Image } from '@/components/Analysis/shared/Image';
import { StyledRemoveButton } from './styles';
import { FileUploadStatus, FileTypes } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { theme } from '@/theme/theme';
import { Attachment } from './type';
import { AttachmentCaption } from '../CaptionBox/AttachmentCaption';

type AttachmentDisplayProps = {
  collectionName: string;
  selectedAttachment: Attachment;
  selectedAttachmentIndex: number;
  showRemoveIcon: boolean;
  handleRemoveAttachment?: (index: number) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  attachmentCaption: string;
  setAttachmentCaption: (caption: string) => void;
  handleAttachmentCaption: () => void;
  handleCaption: (e: React.MouseEvent) => void;
  uploadProgress?: number;
  enableUpload?: boolean;
  cardHeight?: string;
  handleClick?: (e: React.MouseEvent, attachment?: Attachment) => void;
  isUploadDialog?: boolean;
  hasError?: boolean;
  handleError?: (success: boolean) => void;
  location?: string;
};

export const AttachmentDisplay = ({
  collectionName,
  selectedAttachment,
  selectedAttachmentIndex,
  showRemoveIcon,
  handleRemoveAttachment,
  isEditing,
  setIsEditing,
  attachmentCaption,
  setAttachmentCaption,
  handleAttachmentCaption,
  handleCaption,
  uploadProgress = 0,
  cardHeight = '80px',
  handleClick,
  isUploadDialog,
  hasError,
  handleError,
  location = '',
}: AttachmentDisplayProps) => {
  const renderFileContent = () => {
    const isImage =
      selectedAttachment.type.includes('image/') ||
      [FileTypes.Jpeg, FileTypes.Png].includes(selectedAttachment.type as FileTypes);
    const isPdf =
      selectedAttachment.type.includes('application/pdf') ||
      selectedAttachment.type === FileTypes.Pdf;
    const isVideo =
      selectedAttachment.type.includes('video/mp4') || selectedAttachment.type === FileTypes.Mp4;

    if (
      selectedAttachment.url &&
      selectedAttachment.isNew &&
      selectedAttachment.uploadStatus === FileUploadStatus.Inprogress
    ) {
      return (
        <CardContent
          className={`${location}-attachment`}
          sx={{
            display: 'flex',
            height: cardHeight,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: abyssColors.primary[100],
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              variant="determinate"
              value={uploadProgress}
              size={50}
              thickness={5}
              sx={{ color: abyssColors.secondary[500] }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" color="text.secondary">
                {`${Math.round(uploadProgress)}%`}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      );
    } else if (isImage) {
      return (
        <Image
          src={`${selectedAttachment.base64}`}
          alt={selectedAttachment.name}
          imgSx={{ height: cardHeight, objectFit: 'contain' }}
          sx={{
            height: cardHeight,
            cursor: hasError ? 'not-allowed' : 'pointer',
            pointerEvents: hasError ? 'all' : 'auto',
          }}
          onLoadEnd={handleError}
          fallbackSize="2.2rem"
          hideErrorMessage
          className={`${location}-attachment ${location}-attachment-image`}
        />
      );
    } else if (isPdf || isVideo) {
      return (
        <a href={selectedAttachment.url} target="_blank" rel="noopener noreferrer">
          <CardContent
            sx={{
              display: 'flex',
              height: cardHeight,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: abyssColors.primary[100],
            }}
            className={`${location}-attachment ${location}-attachment-${isPdf ? 'pdf' : 'video'}`}
          >
            {isPdf && <img src="/assets/pdf-assets/pdf.png" width={32} height={32} />}
            {isVideo && (
              <SmartDisplayIcon sx={{ fontSize: 45, color: theme.palette.text.secondary }} />
            )}
          </CardContent>
        </a>
      );
    }
  };

  return (
    <>
      <Card
        sx={{ position: 'relative', overflow: 'visible' }}
        onClick={(event) => {
          if (handleClick) handleClick(event, selectedAttachment);
        }}
      >
        {renderFileContent()}
        {showRemoveIcon && handleRemoveAttachment && (
          <StyledRemoveButton
            aria-label="delete"
            onClick={() => handleRemoveAttachment(selectedAttachmentIndex)}
          >
            <CloseIcon style={{ fontSize: '13px' }} />
          </StyledRemoveButton>
        )}
      </Card>
      <AttachmentCaption
        isUploadDialog={isUploadDialog}
        collectionName={collectionName}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        attachmentCaption={attachmentCaption}
        setAttachmentCaption={setAttachmentCaption}
        handleAttachmentCaption={handleAttachmentCaption}
        handleCaption={handleCaption}
        selectedAttachment={selectedAttachment}
        location={location}
      />
    </>
  );
};
